import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  InspectionSchema,
  InspectionCreateInputSchema,
  InspectionUpdateInputSchema,
  type Inspection,
  type InspectionCreateInput,
  type InspectionUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `inspection` (vérification structurée, org-wide).
 *
 * Réplique le PATRON `asset` : l'entrée est VALIDÉE par Zod
 * (`InspectionCreateInputSchema`) AVANT toute écriture Firestore. On n'écrit
 * jamais un document pour découvrir ensuite qu'il est invalide.
 */

export type { InspectionCreateInput };

/** Nom de la collection Firestore de la famille inspection. */
export const INSPECTIONS_COLLECTION = 'inspections';

/**
 * Repository typé pour la famille `inspection`.
 * Toute lecture est validée par `InspectionSchema`, toute écriture par
 * `InspectionCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface InspectionRepository {
  create(input: InspectionCreateInput): Promise<Inspection>;
  update(id: string, patch: InspectionUpdateInput): Promise<Inspection>;
  list(options?: ListOptions): Promise<Inspection[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Inspection | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Inspection>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Inspection>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Inspection>;
}

const inspectionConverter = createConverter<Inspection>((raw: DocumentData) => InspectionSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createInspectionRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): InspectionRepository {
  const col = collection(db, INSPECTIONS_COLLECTION).withConverter(inspectionConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Inspection>(db, col, INSPECTIONS_COLLECTION, getActorId);

  return {
    async create(input: InspectionCreateInput): Promise<Inspection> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = InspectionCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.custodyId !== undefined) optional['custodyId'] = fields.custodyId;
      if (fields.assetId !== undefined) optional['assetId'] = fields.assetId;
      if (fields.placeId !== undefined) optional['placeId'] = fields.placeId;
      if (fields.templateId !== undefined) optional['templateId'] = fields.templateId;
      if (fields.performedByWorkerId !== undefined) optional['performedByWorkerId'] = fields.performedByWorkerId;
      if (fields.performedAt !== undefined) optional['performedAt'] = fields.performedAt;
      if (fields.checkpoints !== undefined) optional['checkpoints'] = fields.checkpoints;
      if (fields.verdict !== undefined) optional['verdict'] = fields.verdict;
      if (fields.followUpTaskIds !== undefined) optional['followUpTaskIds'] = fields.followUpTaskIds;
      if (fields.workOrderId !== undefined) optional['workOrderId'] = fields.workOrderId;
      if (fields.incidentId !== undefined) optional['incidentId'] = fields.incidentId;
      if (fields.documentIds !== undefined) optional['documentIds'] = fields.documentIds;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Inspection);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return InspectionSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: InspectionUpdateInput): Promise<Inspection> {
      // Valider AVANT d'écrire : même doctrine que create.
      const fields = InspectionUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Seules les clés réellement fournies sont écrites (patch partiel, merge).
      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'écriture partielle : une ref convertie
      // exigerait le document complet.
      await updateDoc(doc(db, INSPECTIONS_COLLECTION, id), data);

      // Relire via la ref convertie : le document réel est la source de vérité,
      // et le converter le valide avec InspectionSchema.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`inspection ${id} introuvable après mise à jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Inspection[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Inspection | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
